import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import _ from 'lodash';

import { Grid, Select, MenuItem, FormControl, InputLabel, OutlinedInput } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import { Info as InfoIcon } from '@material-ui/icons';

import * as strings from '../../helpers/strings';
import * as statisticsReducer from '../reducer';
import * as statisticsActionMgr from '../action-mgr';
import StationStatisticsTotalize from './StationStatisticsTotalize'; 

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HeatmapModule from 'highcharts/modules/heatmap';
import TreemapModule from 'highcharts/modules/treemap';
import Exporting from 'highcharts/modules/exporting';
import { gradientColors } from '../../helpers/constants';

import '../../index.css';

HeatmapModule(Highcharts);
TreemapModule(Highcharts);
Exporting(Highcharts);

const sortSetting = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
const CONTROLS_BAR_HEIGHT = 72;
const DEFAULT_CHART_HEIGHT = 650;

class StationStatisticsGraph extends Component {
    constructor(props) {
        super(props);
        this.containerRef = React.createRef();
        this.chartRef = React.createRef();
        this.layoutTimeouts = [];
        this.state = {
            options: {
                title: {
                    text: '',
                },
                plotOptions: {
                    column: {
                        grouping: true,
                        stacking: 'normal', // Enable stacking
                        groupPadding: 0.42, // control group spacing
                        pointPadding: 0.40, // control bar spacing
                        pointWidth: 55,
                        dataLabels: {
                            enabled: true,
                            formatter: function () {
                                return this.y.toLocaleString(); // Format data labels with commas
                            }
                        }
                    },
                },
                credits: {
                    enabled: false,
                },
            },
            facilitySeries: [],
            isolationSeries: [],
            heatmapSortBy: 'capacity', // 'capacity' | 'pr' | 'energy'
            isFullscreen: false,
        };
    }

    componentDidMount() {
        this.initChart();
        document.addEventListener('fullscreenchange', this.handleFullscreenChange);
        window.addEventListener('resize', this.handleResize);
    }

    componentWillUnmount() {
        document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
        window.removeEventListener('resize', this.handleResize);
        this.clearLayoutTimeouts();
    }

    clearLayoutTimeouts = () => {
        this.layoutTimeouts.forEach(clearTimeout);
        this.layoutTimeouts = [];
    };

    updateChartLayout = (isFullscreen = this.state.isFullscreen) => {
        const chart = this.chartRef.current?.chart;
        if (!chart) {
            return;
        }

        const height = this.getChartHeight(isFullscreen);
        chart.update({
            chart: { height },
            exporting: this.getExportingConfig(),
        }, false);
        chart.setSize(null, height, false);
        chart.reflow();

        const treemapSeries = chart.series.find((series) => series.type === 'treemap');
        if (treemapSeries) {
            treemapSeries.setData(treemapSeries.options.data, false);
            chart.redraw(false);
        }
    };

    scheduleChartLayoutUpdate = (isFullscreen) => {
        this.clearLayoutTimeouts();

        const runUpdate = () => {
            this.updateChartLayout(isFullscreen);

            // Treemap layout depends on container width; reflow again after paint.
            const reflowTimeout = setTimeout(() => {
                this.updateChartLayout(isFullscreen);
            }, 150);
            this.layoutTimeouts.push(reflowTimeout);
        };

        const initialTimeout = setTimeout(() => {
            requestAnimationFrame(runUpdate);
        }, 50);
        this.layoutTimeouts.push(initialTimeout);
    };

    getChartHeight = (isFullscreen = this.state.isFullscreen) =>
        isFullscreen ? Math.max(window.innerHeight - CONTROLS_BAR_HEIGHT, 300) : DEFAULT_CHART_HEIGHT;

    handleResize = () => {
        if (this.chartRef.current?.chart) {
            this.updateChartLayout(this.state.isFullscreen);
        }
    };

    handleFullscreenChange = () => {
        const isFullscreen = document.fullscreenElement === this.containerRef.current;
        this.setState({ isFullscreen }, () => {
            this.scheduleChartLayoutUpdate(isFullscreen);
        });
    };

    toggleFullscreen = () => {
        const container = this.containerRef.current;
        if (!container) {
            return;
        }

        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            container.requestFullscreen();
        }
    };

    getExportingConfig = () => {
        const component = this;
        return {
            enabled: true,
            buttons: {
                contextButton: {
                    menuItems: [{
                        textKey: 'viewFullscreen',
                        text: this.state.isFullscreen ? 'Exit Full Screen' : 'View in Full Screen',
                        onclick() {
                            component.toggleFullscreen();
                        },
                    }],
                },
            },
        };
    };

    componentDidUpdate(prevProps) {
        const { selectedStationName, statisticsFilter, otherData, setStatisticsChartView } = this.props;
        // Re-fetch or re-initialize graph data if the station, filter, or data has changed
        if (
            prevProps.selectedStationName !== selectedStationName ||
            prevProps.statisticsFilter !== statisticsFilter ||
            prevProps.otherData !== otherData
        ) {
            this.initChart(); // Assuming initChart is the method that initializes the graph
        }

        // Leave heatmap when type is no longer inverter
        const prevType = (prevProps.statisticsFilter.type || '').toLowerCase();
        const nextType = (statisticsFilter.type || '').toLowerCase();
        if (prevType !== nextType && nextType !== 'inverter' && statisticsFilter.chartView === 'heatmap') {
            setStatisticsChartView('bar');
        }
    }

    isInverterType = () => {
        const type = (this.props.statisticsFilter.type || '').toLowerCase();
        return type === 'inverter';
    }

    getLatestYearFromOtherData = () => {
        const { otherData } = this.props;
        if (!otherData || otherData.length === 0) {
            return null;
        }
        const years = _.uniq(
            otherData.map((d) => {
                const time = String(d.time || '');
                return time.includes('-') ? time.split('-')[0] : time;
            }).filter(Boolean)
        ).sort();
        return years.length > 0 ? years[years.length - 1] : null;
    }

    handleViewModeChange = (event) => {
        const { statisticsFilter, setStatisticsChartView, setSelectYear } = this.props;
        const nextView = event.target.value;

        if (nextView === statisticsFilter.chartView) {
            return;
        }

        if (nextView === 'heatmap' && statisticsFilter.time === 'all') {
            const latestYear = this.getLatestYearFromOtherData();
            if (latestYear && latestYear !== statisticsFilter.time) {
                setSelectYear(latestYear);
            }
        }

        setStatisticsChartView(nextView);
    }

    handleHeatmapSortByChange = (event) => {
        this.setState({ heatmapSortBy: event.target.value });
    }

    getHeatmapSortValue = (row, sortBy) => {
        if (sortBy === 'pr') {
            return Number(row.performanceRatio) || 0;
        }
        if (sortBy === 'energy') {
            return Number(row.energyYields) || 0;
        }
        return Number(row.systemCapacity) || 0;
    }

    initChart = () => {
        const { otherData, statisticsFilter } = this.props;
    
        if (!otherData) {
            return;
        }
    
        let cloneFacilitySeries = _.cloneDeep(this.state.facilitySeries);
        if (otherData) {
            let sortFacility = otherData.slice().sort((a, b) => a.facility.localeCompare(b.facility, undefined, { numeric: true }));
            let facilityGroup = _.groupBy(sortFacility, d => d.facility);
            
            cloneFacilitySeries = Object.entries(facilityGroup).map(([f_key, f_value]) => ({
                name: f_key,
                data: f_value.map(v => {
                    const timeValue = statisticsFilter.time === "all" ? v.time : parseInt(v.time.split("-")[1], 10); // Use month index in monthly view
                    const dataPoint = {
                        x: timeValue,
                        y: v.energyYields,
                        performanceRatio: v.performanceRatio,
                        systemCapacity: v.systemCapacity, // Use individual system capacity for each facility
                        insolation: v.insolation
                    };
                    return dataPoint;
                })
            }));
        }
    
        let cloneIsolationSeries = _.cloneDeep(this.state.isolationSeries);
        if (otherData) {
            let isolationGroup = _.uniqBy(otherData, d => d.time);
            cloneIsolationSeries = [{
                name: "Insolation",
                color: '#E02E44',
                type: 'line',
                data: isolationGroup.map((i) => ({
                    x: parseInt((statisticsFilter.time === "all") ? i.time : i.time.split("-")[1]),
                    y: i.insolation
                }))
            }];
        }
    
        this.setState({
            facilitySeries: cloneFacilitySeries,
            isolationSeries: cloneIsolationSeries,
        });
    }
    

    getGradientColor(index) {
        const gradientColor = gradientColors[index % gradientColors.length];
        return {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
                [0, gradientColor[0]], // light color
                [1, gradientColor[1]]  // dark color
            ]
        };
    }

    buildHeatmapData = () => {
        const { otherData, statisticsFilter } = this.props;
        const { heatmapSortBy } = this.state;
        const heatmapMonth = statisticsFilter.heatmapMonth;

        if (!otherData || !heatmapMonth) {
            return [];
        }

        const mapped = otherData
            .filter((d) => d.time === heatmapMonth)
            .sort((a, b) => {
                const diff = this.getHeatmapSortValue(b, heatmapSortBy) - this.getHeatmapSortValue(a, heatmapSortBy);
                if (diff !== 0) {
                    return diff;
                }
                return sortSetting.compare(a.facility, b.facility);
            })
            .map((d) => {
                const pr = Number(d.performanceRatio) || 0;
                const capacity = Number(d.systemCapacity) || 0;
                const energy = Number(d.energyYields) || 0;
                const sortMetric = this.getHeatmapSortValue(d, heatmapSortBy);
                return {
                    id: d.facility,
                    name: `${d.facility}<br/>${pr.toFixed(1)}%`,
                    value: capacity,
                    colorValue: pr,
                    // Highcharts defaults sortIndex to -value (capacity); set explicitly so PR/energy order wins
                    sortIndex: -sortMetric,
                    custom: {
                        facility: d.facility,
                        systemCapacity: capacity,
                        performanceRatio: pr,
                        energyYields: energy,
                        insolation: Number(d.insolation) || 0,
                    },
                };
            });

        return mapped;
    }

    getHeatmapOptions = (heatmapData) => {
        const { heatmapSortBy } = this.state;
        const chartHeight = this.getChartHeight();
        // squarified reorders by area (capacity); strip respects sort order for PR/energy
        const layoutAlgorithm = heatmapSortBy === 'capacity' ? 'squarified' : 'strip';
        const inverterCount = heatmapData.length;
        const dynamicFontSize =
            inverterCount > 20 ? '14px' :
            inverterCount > 10 ? '15px' :
            inverterCount > 5 ? '16px' : '18px';
        const dynamicFontSizeToolTip =
            inverterCount > 20 ? '17px' :
            inverterCount > 10 ? '18px' :
            inverterCount > 5 ? '19px' : '21px';

        return {
            chart: {
                type: 'treemap',
                height: chartHeight,
                style: {
                    borderRadius: '4px',
                    overflow: 'hidden',
                },
                events: {
                    render: function () {
                        if (this.series && this.series[0] && this.series[0].points) {
                            this.series[0].points.forEach((point) => {
                                if (point.graphic && point.graphic.element) {
                                    point.graphic.element.setAttribute('rx', 5);
                                    point.graphic.element.setAttribute('ry', 5);
                                }
                            });
                        }
                    },
                },
            },
            title: {
                text: heatmapData.length === 0 ? 'No inverter data for selected month' : '',
                style: { fontSize: '18px' },
            },
            colorAxis: {
                min: 0,
                max: 100,
                stops: [
                    [0.0, '#FB4141'],
                    [0.2, '#FF9B2F'],
                    [0.5, '#FFA239'],
                    [0.70, '#FFE700'],
                    [1.0, '#00C853'],
                ],
                labels: {
                    format: '{value}%',
                },
            },
            legend: {
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom',
                symbolWidth: 300,
                symbolHeight: 5,
                itemMarginTop: 0,
                itemStyle: {
                    fontWeight: 'bold',
                    fontSize: '13px',
                },
            },
            tooltip: {
                useHTML: true,
                formatter: function () {
                    const custom = this.point.custom || {};
                    const cap = custom.systemCapacity ?? '--';
                    const pr = custom.performanceRatio ?? '--';
                    const energy = custom.energyYields ?? '--';
                    const bgColor = this.point.color;

                    return `
                        <div style="
                          background: ${bgColor};
                          padding: 10px;
                          border-radius: 5px;
                          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                          min-width: 150px;
                        ">
                          <div style="font-size: ${dynamicFontSizeToolTip}; font-weight: bold;">${this.point.id}</div>
                          <div style="font-size: ${dynamicFontSizeToolTip};">Capacity: <b>${cap} kWp</b></div>
                          <div style="font-size: ${dynamicFontSizeToolTip};">PR: <b>${pr}%</b></div>
                          <div style="font-size: ${dynamicFontSizeToolTip};">Energy: <b>${Number(energy).toLocaleString()} kWh</b></div>
                        </div>
                    `;
                },
            },
            plotOptions: {
                treemap: {
                    layoutAlgorithm,
                    colorByPoint: false,
                    dataLabels: {
                        enabled: true,
                        useHTML: true,
                        align: 'center',
                        verticalAlign: 'middle',
                        overflow: 'none',
                        style: {
                            textOutline: 'none',
                            fontSize: dynamicFontSize,
                            overflow: 'hidden',
                            whiteSpace: 'pre-wrap',
                            textOverflow: 'ellipsis',
                        },
                        formatter: function () {
                            const pr = this.point.colorValue != null
                                ? Number(this.point.colorValue).toFixed(1)
                                : '--';
                            return `<div style="text-align:center;">
                                      <div style="font-weight:bold">${this.point.id}</div>
                                      <div>${pr}%</div>
                                    </div>`;
                        },
                    },
                },
            },
            series: [
                {
                    type: 'treemap',
                    layoutAlgorithm,
                    data: heatmapData,
                },
            ],
            credits: { enabled: false },
            exporting: this.getExportingConfig(),
        };
    }

    render() {
        const { statisticsFilter } = this.props;
        const { heatmapSortBy, isFullscreen } = this.state;
        const chartView = statisticsFilter.chartView || 'bar';
        const isBarView = chartView === 'bar';
        const yAxisTitle = (statisticsFilter.time === "all") ? "kWh/m²/year" : "kWh/m²/month";

        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const xCategories = statisticsFilter.time === "all"
            ? ['', ...monthNames]
            : ['', ...monthNames.map(m => `${m} ${statisticsFilter.time}`)];

        const heatmapData = isBarView ? [] : this.buildHeatmapData();
        const chartOptions = isBarView
            ? {
                ...this.state.options,
                title: {
                    text: '',
                },
                chart: {
                    height: this.getChartHeight(),
                },
                exporting: this.getExportingConfig(),
                xAxis: {
                    categories: xCategories,
                    allowDecimals: false,
                    title: {
                        text: (statisticsFilter.time === "all") ? "Year" : "Months"
                    }
                },
                yAxis: [
                    { // Primary yAxis for energy meters
                        title: {
                            text: "kWh",
                        },
                        labels: {
                            formatter: function () {
                                return this.value.toLocaleString(); // Format yAxis labels with commas
                            }
                        },
                        stackLabels: {
                            enabled: true,
                            style: {
                                fontWeight: 'bold',
                                color: 'gray',
                                textOutline: 'none',
                                marginBottom: '30px'
                            },
                            formatter: function () {
                                let groupedData = {};
                                const currentTime = this.x; // Get the time for the current bar being hovered over
                            
                                // Group data by year-month and set system capacity once for the selected facilities
                                this.axis.chart.series.forEach((series) => {
                                    if (series.type === 'column') {
                                        series.userOptions.data.forEach((dataPoint) => {
                                            // Check for missing properties
                                            if (!dataPoint.x || dataPoint.y === undefined) {
                                                console.warn('Data point missing time or energyYields:', dataPoint);
                                                return; // Skip this data point
                                            }
                            
                                            const timeKey = dataPoint.x; // "2024" for yearly or "2024-09" for monthly
                            
                                            // Only process data for the specific time period of the hovered bar
                                            if (timeKey === currentTime) {
                                                // Initialize group if it doesn't exist for the specific month/year
                                                if (!groupedData[timeKey]) {
                                                    groupedData[timeKey] = {
                                                        sumEnergyYields: 0,
                                                        totalSystemCapacity: 0, // Sum the system capacities of selected facilities
                                                        totalInsolation: 0, // To be set once per month
                                                        facilityCount: 0,
                                                        insolationSet: false // Track if insolation is already set for the month
                                                    };
                                                }                                                
                                                // Accumulate energy yields and system capacity for selected facilities
                                                groupedData[timeKey].sumEnergyYields += dataPoint.y || 0;
                                                groupedData[timeKey].totalSystemCapacity += dataPoint.systemCapacity || 0;

                                                // Set insolation once for the specific month
                                                if (!groupedData[timeKey].insolationSet && dataPoint.insolation) {
                                                    groupedData[timeKey].totalInsolation = dataPoint.insolation;
                                                    groupedData[timeKey].insolationSet = true; // Mark as set
                                                }                                                
                                                groupedData[timeKey].facilityCount++;
                                            }
                                        });
                                    }
                                });
                            
                                // Generate result text only for the specific period related to this bar
                                let resultText = '';
                                const values = groupedData[currentTime];
                                if (values) {
                                    const { sumEnergyYields, totalSystemCapacity, totalInsolation, facilityCount } = values;
                                    let performanceRatio = 0;
                            
                                    // Calculate PR based on available energy yields, consistent system capacity, and monthly insolation
                                    if (facilityCount > 0 && totalSystemCapacity > 0 && totalInsolation > 0) {
                                        const totalSystemCapacityInsolation = totalSystemCapacity * totalInsolation;
                                        performanceRatio = (sumEnergyYields / totalSystemCapacityInsolation) * 100;
                            
                                    }
                            
                                    // Set default values if necessary
                                    performanceRatio = performanceRatio || 0;
                                    const formattedSumEnergyYields = sumEnergyYields !== undefined ? sumEnergyYields : 0;
                                    const roundedPerformanceRatio = parseFloat(performanceRatio.toFixed(0));
                                    const formattedEnergyYieldsLabel = Number(formattedSumEnergyYields).toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    });

                                    // Format result text for the specific period with rounded values
                                    resultText += `${roundedPerformanceRatio}% PR<br>${formattedEnergyYieldsLabel} kWh<br>`;
                                }
                            
                                return resultText;
                            },                                                                                    
                        }
                    },
                    { // Secondary yAxis for insolation
                        min: 0, // Start from 0
                        title: {
                            text: yAxisTitle, // title for the secondary yAxis
                        },
                        opposite: true,
                        labels: {
                            align: 'left', // Align labels to the left
                            x: -25, // Adjust the x position if needed
                            y: 0, // Adjust the y position if needed
                            style: {
                                textOverflow: 'none', // Ensure the text doesn't overflow
                                whiteSpace: 'nowrap' // Prevent wrapping
                            },
                            formatter: function () {
                                return this.value.toLocaleString(); // Format yAxis labels with commas
                            }
                        }
                    }
                ],
                tooltip: {
                    formatter: function () {
                        return `<b>${this.x}</b><br/>${this.series.name}: ${Number(this.y).toLocaleString()} ${this.series.name === 'Insolation' ? yAxisTitle : 'kWh'}`;
                    },
                },
                plotOptions: {
                    column: {
                        grouping: true,
                        stacking: 'normal', // Enable stacking
                        groupPadding: 0.42, // control group spacing
                        pointPadding: 0.40, // control bar spacing
                        pointWidth: 55,
                        dataLabels: {
                            enabled: true,
                            formatter: function () {
                                return this.y.toLocaleString(); // Format data labels with commas
                            }
                        }
                    },
                    line: {
                        dashStyle: 'Dash', // Dotted line
                        dataLabels: {
                            enabled: true
                        },
                        marker: {
                            symbol: 'circle' // Set the marker symbol to circle
                        },
                        enableMouseTracking: true
                    }
                },
                series: [
                    ...this.state.facilitySeries.map((series, index) => ({
                        ...series,
                        color: this.getGradientColor(index),
                        type: 'column',
                        yAxis: 0, // Assign to primary yAxis
                    })),
                    ...this.state.isolationSeries.map(series => ({
                        ...series,
                        type: 'line',
                        yAxis: 1, // Assign to secondary yAxis
                    }))
                ],
            }
            : this.getHeatmapOptions(heatmapData);

        return (
            <Grid container alignItems="center">
                <Grid item xs={12} style={{ margin: '1px 10px'}}>
                    {(this.state.facilitySeries.length > 0 || this.state.isolationSeries.length > 0 || !isBarView) && (
                        <Paper style={{ paddingTop: 15 }}>
                            <div
                                ref={this.containerRef}
                                style={{
                                    width: '100%',
                                    overflow: 'hidden',
                                    ...(isFullscreen ? {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        backgroundColor: '#fff',
                                    } : {}),
                                }}
                            >
                            <HighchartsReact
                                key={`${chartView}-${statisticsFilter.heatmapMonth || ''}-${heatmapSortBy}`}
                                ref={this.chartRef}
                                highcharts={Highcharts}
                                options={chartOptions}
                                containerProps={{
                                    style: {
                                        width: '100%',
                                        overflow: 'hidden',
                                        flex: isFullscreen ? '1 1 auto' : undefined,
                                    },
                                }}
                            />
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px 12px',
                                flexShrink: 0,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {this.isInverterType() && (
                                        <FormControl variant="outlined" style={{ minWidth: 140, marginRight: 12 }}>
                                            <InputLabel htmlFor="chart-view-mode">View Mode</InputLabel>
                                            <Select
                                                value={chartView}
                                                onChange={this.handleViewModeChange}
                                                MenuProps={isFullscreen ? {
                                                    container: this.containerRef.current,
                                                } : undefined}
                                                input={
                                                    <OutlinedInput
                                                        labelWidth={80}
                                                        name="chartViewMode"
                                                        id="chart-view-mode"
                                                    />
                                                }
                                            >
                                                <MenuItem value="bar">Barchart</MenuItem>
                                                <MenuItem value="heatmap">Heatmap</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                    {!isBarView && this.isInverterType() && (
                                        <FormControl variant="outlined" style={{ minWidth: 140 }}>
                                            <InputLabel htmlFor="heatmap-sort-by">Arrange by</InputLabel>
                                            <Select
                                                value={heatmapSortBy}
                                                onChange={this.handleHeatmapSortByChange}
                                                MenuProps={isFullscreen ? {
                                                    container: this.containerRef.current,
                                                } : undefined}
                                                input={
                                                    <OutlinedInput
                                                        labelWidth={80}
                                                        name="heatmapSortBy"
                                                        id="heatmap-sort-by"
                                                    />
                                                }
                                            >
                                                <MenuItem value="capacity">Capacity</MenuItem>
                                                <MenuItem value="pr">PR</MenuItem>
                                                <MenuItem value="energy">Energy</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                </div>
                                <div className="disclaimer-wrap">
                                    <Tooltip
                                        title={
                                            <div style={{ fontSize: '1rem', color: '#F8F8F8', lineHeight: '1.5em' }}>
                                                {strings.lblStatisticsDisclaimer}
                                            </div>
                                        }
                                    >
                                        <InfoIcon className="disclaimer-icon" />
                                    </Tooltip>
                                </div>
                            </div>
                            </div>
                        </Paper>
                    )}
                </Grid>
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    selectedStationName: statisticsReducer.getSelectedStatisticsStationName(state),
    stationTimeOffset: statisticsReducer.getStationTimeOffset(state),
    statisticsTotalize: statisticsReducer.getStatisticsTotalize(state),
    otherData: statisticsReducer.getOtherData(state),
    statisticsFilter: statisticsReducer.getStatisticsFilter(state),
});

const mapActionsToProps = {
    setselectStatisticsStation: statisticsActionMgr.setSelectStatisticsStation,
    getStationTimeOffset: statisticsActionMgr.getStationTimeOffset,
    setSelectYear: statisticsActionMgr.setSelectYear,
    setStatisticsChartView: statisticsActionMgr.setStatisticsChartView,
};

export default connect(mapStateToProps, mapActionsToProps)(
    withRouter(StationStatisticsGraph)
);